import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { TextField } from 'components/Field/TextField';
import { OptionField } from 'components/Field/OptionField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import deepmerge from 'deepmerge';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { filterArrayEmpty } from 'lib/riskbase';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  ALERT,
  NAVIGATE_MODAL_BACK,
  doSetupOrganisationSave,
} from 'store/actions';
import {
  selectUserIsSupport,
  selectUserSelectedOrganisation,
} from 'store/auth/selectors';
import {
  selectBillingCountries,
  selectSetupOrganisation,
} from 'store/remote/selectors';

export const OrganisationBillingModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsOrganisationBillingModal>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();
  const isSupport = useSelector(selectUserIsSupport);

  const organisation = useSelector(selectUserSelectedOrganisation);
  const setupOrganisation = useSelector(selectSetupOrganisation);
  const billingCountries = useSelector(selectBillingCountries);
  const addressFieldKey = React.useRef(0);

  const initValues = React.useRef({
    name: route.params.name,
    address1: route.params.address1,
    address2: route.params.address2,
    city: route.params.city,
    county: route.params.county,
    postcode: route.params.postcode,
    emails: route.params.emails,
    emailsPo: route.params.emailsPo,
    billingCountryIsoCode: route.params.billingCountryIsoCode,
    billingVatNumber: route.params.billingVatNumber,
  });
  const changes = React.useRef(cloneDeep(initValues.current));

  const checkIsDirty = React.useCallback(
    () => !isEqual(initValues.current, changes.current),
    [],
  );
  const isDirty = React.useRef<boolean>(false);
  isDirty.current = checkIsDirty();

  const checkIsAddressDiff = React.useCallback(
    () =>
      organisation?.address.address1 !== changes.current.address1 ||
      organisation?.address.address2 !== changes.current.address2 ||
      organisation?.address.city !== changes.current.city ||
      organisation?.address.county !== changes.current.county ||
      organisation?.address.postcode !== changes.current.postcode,
    [
      organisation?.address.address1,
      organisation?.address.address2,
      organisation?.address.city,
      organisation?.address.county,
      organisation?.address.postcode,
    ],
  );
  const isAddressDiff = React.useRef<boolean>(false);
  isAddressDiff.current = checkIsAddressDiff();

  const billingCountry = billingCountries.find(
    (country) => country.id === changes.current.billingCountryIsoCode,
  );

  const handleChangeAddress = React.useCallback(
    (newChanges: Partial<typeof changes.current>) => {
      changes.current = deepmerge(changes.current, newChanges, {
        arrayMerge: (_, a) => a,
      });
      addressFieldKey.current += 1;
      if (
        isDirty.current !== checkIsDirty() ||
        isAddressDiff.current !== checkIsAddressDiff()
      ) {
        forceUpdate();
      }
    },
    [checkIsAddressDiff, checkIsDirty, forceUpdate],
  );

  const handleChangeWithName = React.useCallback(
    <
      N extends keyof typeof changes.current,
      V extends (typeof changes.current)[N],
    >(
      name: N,
      value: V,
    ) => {
      changes.current[name] = value;
      forceUpdate();
    },
    [forceUpdate],
  );

  const handleSave = React.useCallback(() => {
    if (!changes.current.name.trim()) {
      dispatch(
        ALERT({
          text: `Name is required`,
        }),
      );
      return;
    }
    const billingVatNumber = changes.current.billingVatNumber
      .trim()
      .toUpperCase();
    if (billingCountry?.requiresVatNumber && !billingVatNumber) {
      dispatch(
        ALERT({
          text: `A VAT number is required for an organisation in ${billingCountry.name}`,
        }),
      );
      return;
    }
    if (isDirty.current) {
      dispatch(
        doSetupOrganisationSave({
          changes: {
            billingAddress: {
              name: changes.current.name,
              address1: changes.current.address1,
              address2: changes.current.address2,
              city: changes.current.city,
              county: changes.current.county,
              postcode: changes.current.postcode,
            },
            billingEmails: changes.current.emails,
            billingEmailsPo: changes.current.emailsPo,
            billingCountryIsoCode: changes.current.billingCountryIsoCode,
            billingVatNumber,
          },
        }),
      );
      return;
    }
    dispatch(
      NAVIGATE_MODAL_BACK([ModalScreens.SettingsOrganisationBillingModal]),
    );
  }, [billingCountry?.name, billingCountry?.requiresVatNumber, dispatch]);

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, handleSave, dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  if (!organisation || !setupOrganisation) {
    return null;
  }

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Billing"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card label="Name" required>
        {isSupport ? (
          <TextField
            placeholder="..."
            value={changes.current.name}
            onChangeWithName={handleChangeWithName}
            name="name"
            autoCapitalize="words"
            autoCorrect={false}
            onEsc={handleClose}
            autoFocusWeb
            autoFocusDelay
          />
        ) : (
          <CardRow value={changes.current.name} />
        )}
      </Card>

      <Card label="Country">
        <OptionField
          modalLabel="Billing Country"
          label="Country"
          value={[changes.current.billingCountryIsoCode]}
          options={billingCountries.map((country) => ({
            internalValue: country.id,
            name: country.name,
          }))}
          onChange={(countryIsoCodes) =>
            handleChangeWithName(
              'billingCountryIsoCode',
              countryIsoCodes[0] || changes.current.billingCountryIsoCode,
            )
          }
          forceModal
          modalSearchable
          showFieldTypeIcon={false}
        />
      </Card>
      <Card
        label="VAT Number"
        required={billingCountry?.requiresVatNumber}
        explanation={
          billingCountry?.requiresVatNumber
            ? 'A VAT number is required for this billing country'
            : ''
        }
      >
        <TextField
          placeholder={
            billingCountry?.requiresVatNumber
              ? 'VAT Number (Required)'
              : 'VAT Number'
          }
          value={changes.current.billingVatNumber}
          name="billingVatNumber"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="characters"
          autoCorrect={false}
          onEsc={handleClose}
          maxLength={20}
        />
      </Card>

      <Card
        label="Address"
        explanationLinkText={`Copy from details: ${[
          organisation.address.address1,
          organisation.address.address2,
          organisation.address.city,
          organisation.address.county,
          organisation.address.postcode,
        ]
          .filter(filterArrayEmpty)
          .join(', ')}`}
        onExplanationLinkPress={
          isAddressDiff.current
            ? () =>
                handleChangeAddress({
                  address1: organisation.address.address1,
                  address2: organisation.address.address2,
                  city: organisation.address.city,
                  county: organisation.address.county,
                  postcode: organisation.address.postcode,
                })
            : undefined
        }
      >
        <TextField
          placeholder="Address 1"
          value={changes.current.address1}
          name="address1"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="words"
          autoCorrect={false}
          onEsc={handleClose}
          key={addressFieldKey.current}
        />
        <TextField
          placeholder="Address 2"
          value={changes.current.address2}
          name="address2"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="words"
          autoCorrect={false}
          onEsc={handleClose}
          key={addressFieldKey.current}
        />
        <TextField
          placeholder="City"
          value={changes.current.city}
          name="city"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="words"
          autoCorrect={false}
          onEsc={handleClose}
          key={addressFieldKey.current}
        />
        <TextField
          placeholder="County"
          value={changes.current.county}
          name="county"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="words"
          autoCorrect={false}
          onEsc={handleClose}
          key={addressFieldKey.current}
        />
        <TextField
          placeholder="Postcode"
          value={changes.current.postcode}
          name="postcode"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="words"
          autoCorrect={false}
          onEsc={handleClose}
          key={addressFieldKey.current}
        />
      </Card>

      <Card
        label="Emails"
        explanation="Comma separated list of emails. All invoices and billing updates will be sent to these addresses"
      >
        <TextAreaField
          placeholder="No Emails"
          value={changes.current.emails.split(',').join(', ')}
          name="emails"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="none"
          autoCorrect={false}
          onEsc={handleClose}
          maxLength={512}
        />
      </Card>

      <Card
        label="PO Emails"
        explanation="Comma separated list of emails. Only invoices ***with*** PO numbers will be sent to these addresses in addition to the emails listed above"
      >
        <TextAreaField
          placeholder="No Emails"
          value={changes.current.emailsPo.split(',').join(', ')}
          name="emailsPo"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="none"
          autoCorrect={false}
          onEsc={handleClose}
          maxLength={512}
        />
      </Card>
    </Page>
  );
};
